import React, { useState, useEffect } from 'react';
import './moodle-integration.css';

/**
 * MoodleIntegration component for connecting and deploying models to Moodle LMS
 * 
 * This component provides a UI for:
 * 1. Connecting to a Moodle instance with API token
 * 2. Selecting courses to deploy models to
 * 3. Configuring model deployment options
 * 4. Deploying models to Moodle courses
 */
const MoodleIntegration = ({ user = null, onDeploy = null }) => {
  // State for connection configuration
  const [moodleUrl, setMoodleUrl] = useState('');
  const [token, setToken] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('');
  const [connectorId, setConnectorId] = useState(null);
  
  // State for course selection and deployment
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [activities, setActivities] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [deploymentStatus, setDeploymentStatus] = useState('idle'); // idle, deploying, success, error
  const [deploymentOptions, setDeploymentOptions] = useState({
    enableGrading: true,
    adaptToLearningStyles: true,
    enableBiasReporting: true,
    enableTokenEconomy: true
  });
  
  // State for saved configurations
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [loadingSavedConfigs, setLoadingSavedConfigs] = useState(false);

  // Load saved configurations on component mount
  useEffect(() => {
    loadSavedConfigurations();
  }, []);

  // Load any previously saved Moodle configurations
  const loadSavedConfigurations = async () => {
    try {
      setLoadingSavedConfigs(true);
      const response = await fetch('/api/moodle/configs');
      const data = await response.json();
      
      if (data.success && data.configs) {
        setSavedConfigs(data.configs);
        
        // If there's a saved configuration, pre-fill the form
        if (data.configs.length > 0) {
          setMoodleUrl(data.configs[0].site_url || '');
          setToken(data.configs[0].token || '');
        }
      }
    } catch (error) {
      console.error('Error loading saved Moodle configurations:', error);
    } finally {
      setLoadingSavedConfigs(false);
    }
  };

  // Save current configuration for later use
  const saveConfiguration = async () => {
    if (!moodleUrl || !token) {
      setConnectionStatus('Moodle URL and API token are required');
      return;
    }

    try {
      const response = await fetch('/api/moodle/save-config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_url: moodleUrl,
          token: token
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setConnectionStatus('Configuration saved successfully');
        // Reload saved configurations
        loadSavedConfigurations();
      } else {
        setConnectionStatus(`Error: ${data.error}`);
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      setConnectionStatus('Error saving configuration');
    }
  };

  // Connect to Moodle with the provided credentials
  const connectToMoodle = async () => {
    if (!moodleUrl || !token) {
      setConnectionStatus('Moodle URL and API token are required');
      return;
    }

    try {
      setConnectionStatus('Connecting to Moodle...');
      
      const response = await fetch('/api/moodle/connect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_url: moodleUrl,
          token: token,
          demo_mode: false // Set to true for testing
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setConnectionStatus('Connected to Moodle successfully');
        setConnectorId(data.connector_id);
        
        // Save configuration for future use
        saveConfiguration();
        
        // Fetch courses
        fetchCourses(data.connector_id);
      } else {
        setConnectionStatus(`Connection failed: ${data.error}`);
      }
    } catch (error) {
      console.error('Error connecting to Moodle:', error);
      setConnectionStatus('Error connecting to Moodle');
    }
  };

  // Fetch courses from connected Moodle instance
  const fetchCourses = async (connId) => {
    try {
      const response = await fetch(`/api/moodle/courses?connector_id=${connId || connectorId}`);
      const data = await response.json();
      
      if (data.success && data.courses) {
        setCourses(data.courses);
        
        // Select first course by default
        if (data.courses.length > 0) {
          setSelectedCourse(data.courses[0].id);
          fetchActivities(connId || connectorId, data.courses[0].id);
        }
      } else {
        console.error('Failed to fetch courses:', data.error);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  // Fetch activities for a selected course
  const fetchActivities = async (connId, courseId) => {
    try {
      const response = await fetch(`/api/moodle/course/${courseId}/activities?connector_id=${connId || connectorId}`);
      const data = await response.json();
      
      if (data.success && data.activities) {
        setActivities(data.activities);
      } else {
        console.error('Failed to fetch activities:', data.error);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  // Handle course selection change
  const handleCourseChange = (e) => {
    const courseId = e.target.value;
    setSelectedCourse(courseId);
    
    if (courseId) {
      fetchActivities(connectorId, courseId);
    } else {
      setActivities([]);
    }
  };

  // Handle activity selection change
  const handleActivitySelection = (activityId) => {
    setSelectedActivities(prev => {
      if (prev.includes(activityId)) {
        return prev.filter(id => id !== activityId);
      } else {
        return [...prev, activityId];
      }
    });
  };

  // Deploy model to selected course
  const deployModel = async (modelId) => {
    if (!connectorId || !selectedCourse) {
      alert('Please connect to Moodle and select a course first');
      return;
    }
    
    try {
      setDeploymentStatus('deploying');
      
      const response = await fetch('/api/moodle/deploy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${connectorId}`
        },
        body: JSON.stringify({
          model_id: modelId || 'neo_algebra',
          course_id: selectedCourse,
          activity_ids: selectedActivities.length > 0 ? selectedActivities : undefined,
          options: deploymentOptions
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setDeploymentStatus('success');
        
        // Notify parent component
        if (onDeploy) {
          onDeploy({ 
            success: true, 
            connector_id: connectorId,
            course_id: selectedCourse,
            model_id: modelId || 'neo_algebra',
            integration: data.integration
          });
        }
      } else {
        setDeploymentStatus('error');
        console.error('Deployment failed:', data.error);
      }
    } catch (error) {
      console.error('Error deploying model:', error);
      setDeploymentStatus('error');
    }
  };

  // Toggle deployment options
  const toggleOption = (option) => {
    setDeploymentOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  // Use a saved configuration
  const useSavedConfig = (config) => {
    setMoodleUrl(config.site_url || '');
    setToken(config.token || '');
  };

  return (
    <div className="moodle-integration-container">
      <div className="moodle-header">
        <h2>Moodle Integration</h2>
        <p>Deploy Morpheus-RL AI tutors to your Moodle courses</p>
      </div>
      
      {/* Connection Section */}
      <div className="connection-section">
        <h3>Connect to Moodle</h3>
        
        {/* Saved Configurations */}
        {savedConfigs.length > 0 && (
          <div className="saved-configs">
            <label>Saved Configurations</label>
            <div className="config-list">
              {savedConfigs.map((config, index) => (
                <button 
                  key={index} 
                  className="config-item"
                  onClick={() => {
                    // Instead of calling the hook inside a callback, use its functionality directly
                    setMoodleUrl(config.site_url);
                    setToken(config.token);
                    // Other state you need to set from the saved config
                  }}
                >
                  {config.site_url}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Connection Form */}
        <div className="connection-form">
          <div className="form-group">
            <label htmlFor="moodle-url">Moodle Site URL</label>
            <input
              id="moodle-url"
              type="text"
              value={moodleUrl}
              onChange={(e) => setMoodleUrl(e.target.value)}
              placeholder="https://your-moodle-site.com"
              disabled={!!connectorId}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="moodle-token">Web Services Token</label>
            <input
              id="moodle-token"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Your Moodle API token"
              disabled={!!connectorId}
            />
            <small className="help-text">
              Create a token in Moodle: Site Administration &gt; Plugins &gt; Web Services &gt; Manage tokens
            </small>
          </div>
          
          <div className="connection-actions">
            <button 
              className="save-btn"
              onClick={saveConfiguration}
              disabled={!!connectorId || !moodleUrl || !token}
            >
              Save Configuration
            </button>
            
            <button 
              className="connect-btn"
              onClick={connectToMoodle}
              disabled={!!connectorId || !moodleUrl || !token}
            >
              Connect to Moodle
            </button>
          </div>
          
          {connectionStatus && (
            <div className={`connection-status ${connectorId ? 'success' : ''}`}>
              {connectionStatus}
            </div>
          )}
        </div>
      </div>
      
      {/* Course Selection - Only visible after connecting */}
      {connectorId && (
        <div className="course-selection-section">
          <h3>Select Course</h3>
          
          <div className="form-group">
            <label htmlFor="course-select">Course</label>
            <select 
              id="course-select" 
              value={selectedCourse} 
              onChange={handleCourseChange}
            >
              <option value="">Select a course...</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Activity Selection */}
          {selectedCourse && activities.length > 0 && (
            <div className="activity-selection">
              <h4>Course Activities (Optional)</h4>
              <p className="help-text">Select specific activities to integrate with, or leave unselected to create a new activity</p>
              
              <div className="activity-list">
                {activities.map(activity => (
                  <div key={activity.id} className="activity-item">
                    <input
                      type="checkbox"
                      id={`activity-${activity.id}`}
                      checked={selectedActivities.includes(activity.id)}
                      onChange={() => handleActivitySelection(activity.id)}
                    />
                    <label htmlFor={`activity-${activity.id}`}>
                      {activity.name} <span className="activity-type">({activity.type})</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Deployment Options */}
          {selectedCourse && (
            <div className="deployment-options">
              <h4>Deployment Options</h4>
              
              <div className="options-grid">
                <div className="option-item">
                  <input
                    type="checkbox"
                    id="option-grading"
                    checked={deploymentOptions.enableGrading}
                    onChange={() => toggleOption('enableGrading')}
                  />
                  <label htmlFor="option-grading">Enable grading integration</label>
                </div>
                
                <div className="option-item">
                  <input
                    type="checkbox"
                    id="option-learning-styles"
                    checked={deploymentOptions.adaptToLearningStyles}
                    onChange={() => toggleOption('adaptToLearningStyles')}
                  />
                  <label htmlFor="option-learning-styles">Adapt to student learning styles</label>
                </div>
                
                <div className="option-item">
                  <input
                    type="checkbox"
                    id="option-bias"
                    checked={deploymentOptions.enableBiasReporting}
                    onChange={() => toggleOption('enableBiasReporting')}
                  />
                  <label htmlFor="option-bias">Enable bias reporting</label>
                </div>
                
                <div className="option-item">
                  <input
                    type="checkbox"
                    id="option-token"
                    checked={deploymentOptions.enableTokenEconomy}
                    onChange={() => toggleOption('enableTokenEconomy')}
                  />
                  <label htmlFor="option-token">Enable token economy</label>
                </div>
              </div>
            </div>
          )}
          
          {/* Deployment Button */}
          {selectedCourse && (
            <div className="deployment-actions">
              <button
                className={`deploy-btn ${deploymentStatus}`}
                onClick={() => deployModel()}
                disabled={deploymentStatus === 'deploying' || deploymentStatus === 'success'}
              >
                {deploymentStatus === 'deploying' ? 'Deploying...' : 
                 deploymentStatus === 'success' ? 'Deployed Successfully' : 
                 'Deploy to Moodle'}
              </button>
              
              {deploymentStatus === 'error' && (
                <div className="deployment-error">
                  Deployment failed. Please try again or check the console for errors.
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Documentation Section */}
      {!connectorId && (
        <div className="documentation-section">
          <h3>Getting Started with Moodle Integration</h3>
          
          <div className="setup-steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Enable Web Services</h4>
                <p>In Moodle, go to Site Administration &gt; Advanced features and enable web services</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>Create a Web Service</h4>
                <p>Go to Site Administration &gt; Plugins &gt; Web services &gt; External services</p>
                <p>Create a new custom service with all the required functions for integration</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>Generate a Token</h4>
                <p>Go to Site Administration &gt; Plugins &gt; Web services &gt; Manage tokens</p>
                <p>Create a token for the web service created in step 2</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h4>Connect and Deploy</h4>
                <p>Enter your Moodle URL and token above to connect</p>
                <p>Once connected, select a course and deploy your model</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MoodleIntegration;